import { createFileRoute } from '@tanstack/react-router';
import { Box, Container, Typography, Stack, Card, CardContent, Grid } from '@mui/material';
import { useMemo } from 'react';
import { z } from 'zod';
import quotesData from '@/data/quotes.json';
import { pageColors, getGradient } from '@/theme/colors';
import { quoteStickyStyle, type QuoteStickyStyle } from '@/lib/quoteStickies';
import { useThemeContext } from '@/contexts/ThemeContext';
import { createPageMeta, createCollectionJsonLd, PAGE_KEYWORDS } from '@/lib/meta';
import { analytics } from '@/lib/analytics';

export const Route = createFileRoute('/quotes')({
  component: QuotesPage,
  head: () => {
    const base = createPageMeta({
      title: 'Quotes - Believe it; see it.',
      description: 'Inspirational quotes from various sources, displayed as sticky notes.',
      path: '/quotes',
      image: '/og/quotes.png',
      imageAlt: 'Quotes — Christian Battaglia',
      keywords: PAGE_KEYWORDS.quotes,
    });
    return {
      ...base,
      scripts: [
        createCollectionJsonLd({
          name: 'Quotes — Believe it; see it.',
          description: 'Inspirational quotes from various sources, displayed as sticky notes.',
          path: '/quotes',
          keywords: PAGE_KEYWORDS.quotes,
        }),
      ],
    };
  },
});

// Zod schema for runtime validation
const QuoteSchema = z.object({
  text: z.string(),
  author: z.string(),
  from: z.string().optional(),
  added: z.string().optional(),
  uttered: z.string().optional(),
  link: z.string().url().optional(),
});

const QuotesArraySchema = z.array(QuoteSchema);

type Quote = z.infer<typeof QuoteSchema>;

// Validate quotes at module load time
const validatedQuotes = QuotesArraySchema.parse(quotesData);

function StickyNote({ quote, style }: { quote: Quote; style: QuoteStickyStyle }) {
  return (
    <Box
      onClick={() => analytics.quoteClicked(quote.author, quote.text.length)}
      sx={{
        margin: '10px',
        padding: '20px 24px',
        minHeight: '200px',
        width: `${style.width}px`,
        backgroundColor: style.backgroundColor,
        color: style.color,
        boxShadow: '0 3px 6px rgba(0,0,0,0.25)',
        border: '1px solid rgba(0,0,0,0.25)',
        transform: `rotate(${style.rotate})`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        cursor: 'pointer',
        '&:hover': {
          transform: `rotate(0deg) scale(1.02)`,
          boxShadow: '0 6px 12px rgba(0,0,0,0.3)',
          zIndex: 10,
        },
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontFamily: '"Kalam", "Comic Sans MS", cursive',
          fontSize: '1.1rem',
          lineHeight: 1.6,
          mb: 2,
          whiteSpace: 'pre-wrap',
        }}
      >
        {quote.text}
      </Typography>

      <Stack spacing={0.5}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: '"Kalam", "Comic Sans MS", cursive',
            fontStyle: 'italic',
            fontSize: '0.95rem',
            textAlign: 'right',
          }}
        >
          — {quote.author}
        </Typography>
        {quote.from && quote.from !== quote.author && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              textAlign: 'right',
              opacity: 0.8,
            }}
          >
            via {quote.from}
          </Typography>
        )}
        {quote.added && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              textAlign: 'right',
              opacity: 0.6,
            }}
          >
            {new Date(quote.added).getFullYear()}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function QuotesPage() {
  const { mode } = useThemeContext();

  const stickyStyles = useMemo(
    () => validatedQuotes.map((quote, index) => quoteStickyStyle(quote.text, index)),
    [],
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: getGradient(pageColors.quotes, mode),
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left: Name & Stats */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={3} alignItems={{ xs: 'center', md: 'flex-start' }} textAlign={{ xs: 'center', md: 'left' }}>
                {/* Name */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 400,
                      opacity: 0.8,
                      letterSpacing: '0.5px',
                    }}
                  >
                    quotes of
                  </Typography>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.2,
                    }}
                  >
                    Christian Battaglia
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                    @cdbattags
                  </Typography>
                </Box>

                {/* Quotes Stats Card */}
                <Card
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '100%',
                    boxShadow: 'none',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={1.5}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          fontSize: '0.75rem',
                          mb: 0.5,
                        }}
                      >
                        Collection
                      </Typography>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          {validatedQuotes.length} quotes
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Words to live by
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          {new Set(validatedQuotes.map(q => q.author)).size} authors
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Diverse voices
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {/* Right: Description */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Stack spacing={3}>
                <Typography variant="h5" sx={{ opacity: 0.95, fontWeight: 600 }}>
                  Quote the Walls
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    opacity: 0.95,
                  }}
                >
                  "Believe it; see it." — A curated collection of quotes that have shaped my thinking, inspired action,
                  and served as guiding principles in both life and work.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    opacity: 0.95,
                  }}
                >
                  From timeless wisdom to modern insights, these words are reminders of what matters most.
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sticky Notes */}
      <Box
        sx={{
          display: 'flex',
          padding: '20px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          minHeight: '500px',
        }}
      >
        {validatedQuotes.map((quote, index) => (
          <StickyNote key={index} quote={quote} style={stickyStyles[index]!} />
        ))}
      </Box>
    </Box>
  );
}
